import React, { useCallback, useEffect, useState } from 'react';
import { LoadingSpinner } from '../../common';
import { usePermission } from '../../../hooks/usePermission';
import { EdiService, formatCents, formatEdiDate } from '../../../services/billing/edi.service';
import type { EdiClaimTimeline, EdiClaimTx } from '../../../dtos/billing/edi.dto';
import { BILLING_SUBMIT, Cell, EdiGate, ErrorNote, SimulatedBadge, errorText } from './ediUi';

type Tab = 'timeline' | 'lines' | 'touchless';

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'timeline', label: 'Timeline' },
  { key: 'lines', label: 'Lines' },
  { key: 'touchless', label: 'Why not touchless' },
];

function when(at: string): string {
  const d = new Date(at);
  return Number.isNaN(d.getTime()) ? at : d.toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' });
}

function actorLabel(tx: EdiClaimTx): string {
  if (tx.actor.type === 'user') return tx.actor.id ? `User ${tx.actor.id}` : 'User';
  if (tx.actor.type === 'operator') return 'Pelora billing team';
  return 'System';
}

export const ClaimTimelineList: React.FC<{ transactions: EdiClaimTx[] }> = ({ transactions }) =>
  transactions.length === 0 ? (
    <p className="text-sm text-slate-500">No transactions yet.</p>
  ) : (
    <ol className="space-y-2" aria-label="Claim timeline">
      {transactions.map((tx, i) => (
        <li key={`${tx.at}-${tx.kind}-${i}`} className="rounded-md border border-slate-200 p-2 text-sm" data-testid="claim-tx">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded bg-slate-800 px-1.5 py-0.5 text-xs font-semibold text-white">{tx.kind}</span>
            <span className="font-medium text-slate-800">{tx.outcome}</span>
            {tx.toStatus && <span className="text-xs text-slate-500">{tx.fromStatus ? `${tx.fromStatus} → ` : '→ '}{tx.toStatus}</span>}
            {tx.simulated && <SimulatedBadge />}
          </div>
          {tx.message && <p className="mt-1 break-words text-slate-700">{tx.message}</p>}
          {tx.reasonCodes.length > 0 && <p className="mt-1 text-xs text-slate-600">Codes: {tx.reasonCodes.join(', ')}</p>}
          <p className="mt-1 text-xs text-slate-500">{when(tx.at)} · {tx.source} · {actorLabel(tx)}{tx.ruleId ? ` · rule ${tx.ruleId}` : ''}</p>
        </li>
      ))}
    </ol>
  );

const EdiClaimSectionInner: React.FC<{ claimId: string }> = ({ claimId }) => {
  const canSubmit = usePermission(BILLING_SUBMIT);
  const [tab, setTab] = useState<Tab>('timeline');
  const [data, setData] = useState<EdiClaimTimeline | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await EdiService.getClaimTimeline(claimId));
    } catch (err) {
      setError(errorText(err, 'Failed to load the claim timeline'));
    }
  }, [claimId]);

  useEffect(() => { void load(); }, [load]);

  const resubmit = async () => {
    setBusy(true);
    setError(null);
    try {
      await EdiService.resubmitClaim(claimId);
      await load();
    } catch (err) {
      setError(errorText(err, 'Failed to resubmit the claim'));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return error ? <ErrorNote message={error} /> : <LoadingSpinner label="Loading claim timeline" />;
  const { claim, transactions, lines, touchlessReasons } = data;
  const sub = claim.submission;

  return (
    <section className="mt-4 rounded-lg border border-slate-200 p-3" aria-label="Pelora Billing">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="text-xs font-semibold uppercase text-slate-500">Pelora Billing</h4>
        {claim.simulated && <SimulatedBadge />}
        {claim.filingDeadline && <span className="text-xs text-slate-600">Filing deadline {formatEdiDate(claim.filingDeadline)}</span>}
      </div>
      {sub && (
        <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-2" aria-label="Control numbers">
          <div><dt className="inline text-slate-500">Pelora control #: </dt><dd className="inline break-all">{sub.controlNumber || '—'}</dd></div>
          <div><dt className="inline text-slate-500">Payer claim #: </dt><dd className="inline break-all">{sub.payerClaimControlNumber || '—'}</dd></div>
          <div><dt className="inline text-slate-500">Route: </dt><dd className="inline">{sub.payerRoute || '—'} · {sub.method || '—'}</dd></div>
          <div><dt className="inline text-slate-500">Attempt: </dt><dd className="inline">{sub.attempt}</dd></div>
        </dl>
      )}
      {claim.adjudication && (
        <p className="mt-2 text-sm text-slate-700">
          Paid {formatCents(claim.adjudication.paidCents)} of allowed {formatCents(claim.adjudication.allowedCents)}
          {claim.adjudication.patientRespCents > 0 ? ` · patient responsibility ${formatCents(claim.adjudication.patientRespCents)}` : ''}
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-1 border-b border-slate-100" role="tablist" aria-label="Claim billing detail">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 text-sm ${tab === t.key ? 'border-b-2 border-slate-800 font-medium text-slate-900' : 'text-slate-500'}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div role="tabpanel" className="mt-2">
        <ErrorNote message={error} />
        {tab === 'timeline' && <ClaimTimelineList transactions={transactions} />}
        {tab === 'lines' && (
          lines.length === 0 ? <p className="text-sm text-slate-500">No adjudicated lines yet.</p> : (
            <div className="text-sm" aria-label="Line adjudication">
              <div className="hidden grid-cols-8 gap-2 border-b border-slate-100 pb-1 text-xs uppercase text-slate-500 sm:grid">
                <span>Line</span><span>DOS</span><span>Code</span><span className="text-right">Billed</span><span className="text-right">Allowed</span><span className="text-right">Paid</span><span>CARC / RARC</span><span>Status</span>
              </div>
              {lines.map((l) => (
                <div key={l.lineNo} className="grid grid-cols-2 gap-1 border-b border-slate-100 py-2 sm:grid-cols-8 sm:gap-2">
                  <Cell label="Line">{l.lineNo}</Cell>
                  <Cell label="DOS">{formatEdiDate(l.serviceDate)}</Cell>
                  <Cell label="Code">{[l.hcpcs, ...l.modifiers].join(' ')} · {l.units} u</Cell>
                  <Cell label="Billed" className="sm:text-right">{formatCents(l.chargeCents)}</Cell>
                  <Cell label="Allowed" className="sm:text-right">{formatCents(l.allowedCents)}</Cell>
                  <Cell label="Paid" className="sm:text-right">{formatCents(l.paidCents)}</Cell>
                  <Cell label="CARC / RARC">{[l.groupCode, ...l.carc].filter(Boolean).join(' ') || '—'}{l.rarc.length ? ` / ${l.rarc.join(', ')}` : ''}</Cell>
                  <Cell label="Status">{l.status}</Cell>
                </div>
              ))}
            </div>
          )
        )}
        {tab === 'touchless' && (
          claim.touchless === true ? <p className="text-sm text-green-800">This claim was paid with no manual step.</p>
            : touchlessReasons.length === 0 ? <p className="text-sm text-slate-500">No reasons recorded yet.</p>
              : <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700" aria-label="Why not touchless">{touchlessReasons.map((r) => <li key={r}>{r}</li>)}</ul>
        )}
      </div>

      {canSubmit && claim.status === 'REJECTED' && (
        <button type="button" disabled={busy} onClick={resubmit} className="mt-3 rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 disabled:opacity-50">
          Resubmit
        </button>
      )}
    </section>
  );
};

/** Register claim detail additions (Timeline, Lines, Why not touchless). Nothing renders when billingEdi is off. */
const EdiClaimSection: React.FC<{ claimId: string }> = ({ claimId }) => (
  <EdiGate>
    <EdiClaimSectionInner claimId={claimId} />
  </EdiGate>
);

export default EdiClaimSection;
